// PE-6139 — Attach Firebase anonymous Bearer for guest PE2 calls only (never after sign-in)
import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { from, switchMap } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthStateService } from '../services/auth-state.service';
import { GuestFirebaseAuthService } from '../services/guest-firebase-auth.service';

/** PE2 BFF host (getpatientcount, guest registration, self-scheduling, captcha). */
function isPeBackendRequest(url: string): boolean {
  const base = String(environment.pebackend_url ?? '').replace(/\/$/, '');
  if (!base) {
    return false;
  }
  return url.startsWith(base) || url.startsWith(base + '/');
}

/**
 * Patient-service PE2 intake (e.g. patientByDemographics).
 * Built from apiUrl (.../qpatientportal → .../patient), not pebackend_url.
 */
function isPatientPe2IntakeRequest(url: string): boolean {
  const patientBase = String(environment.apiUrl ?? '')
    .replace(/\/$/, '')
    .replace(/\/qpatientportal$/i, '/patient');
  if (!patientBase) {
    return false;
  }
  return url.startsWith(patientBase) && url.includes('/pe2/intake/');
}

/** Guest anonymous Bearer is required only for these PE2 guest endpoints. */
function needsGuestAnonymousBearer(url: string): boolean {
  return isPeBackendRequest(url) || isPatientPe2IntakeRequest(url);
}

export const guestAuthInterceptor: HttpInterceptorFn = (req, next) => {
  const guestAuth = inject(GuestFirebaseAuthService);
  const authState = inject(AuthStateService);

  // PE-6139 — signed-in / MFA / authGuard sessions: never attach or create anonymous Bearer
  if (authState.isLoggedIn || guestAuth.hasPortalSession()) {
    return next(req);
  }

  // PE-6139 — guest PE2 only (pebackend + patient /pe2/intake/*)
  if (!needsGuestAnonymousBearer(req.url)) {
    return next(req);
  }

  const guestPi = guestAuth.getGuestPi();
  if (guestPi?.token && !guestAuth.isTokenExpired(guestPi.token)) {
    return next(
      req.clone({
        setHeaders: {
          Authorization: `Bearer ${guestPi.token}`,
        },
      })
    );
  }

  // Missing or expired — create/refresh once (shared in-flight promise in service)
  return from(guestAuth.ensureAnonymousSession()).pipe(
    switchMap((refreshed) =>
      next(
        req.clone({
          setHeaders: {
            Authorization: `Bearer ${refreshed.token}`,
          },
        })
      )
    )
  );
};
